interface ErrorLikeResponse {
    status?: number;
    data?: unknown;
}

interface ErrorLike {
    isAxiosError?: unknown;
    response?: ErrorLikeResponse;
    message?: unknown;
}

const statusMessages: Record<number, string> = {
    401: "Your session has expired. Sign in again and retry.",
    403: "You're not authorized to perform this action.",
    404: "That action isn't available. Refresh the page and try again.",
    405: "That action isn't available. Refresh the page and try again.",
    409: "Someone else changed this record. Refresh the page and try again.",
    429: "Too many requests. Wait a moment and try again.",
};

function firstNonEmptyString(values: unknown[]): string | null {
    for (const value of values) {
        if (typeof value === "string" && value.trim().length) return value;
    }
    return null;
}

function fromValidationProblemDetails(data: Record<string, unknown>): string | null {
    const errors = data.errors;
    if (!errors || typeof errors !== "object") return null;

    for (const value of Object.values(errors as Record<string, unknown>)) {
        if (Array.isArray(value)) {
            const first = firstNonEmptyString(value);
            if (first) return first;
        } else if (typeof value === "string" && value.trim().length) {
            return value;
        }
    }
    return null;
}

function fromBody(data: unknown): string | null {
    if (typeof data === "string") {
        const trimmed = data.trim();
        if (!trimmed.length) return null;
        // An HTML/XML error page is never useful copy.
        return trimmed.startsWith("<") ? null : data;
    }

    if (!data || typeof data !== "object") return null;

    const record = data as Record<string, unknown>;
    // `title` last: an [ApiController] 400 always carries boilerplate there, which would shadow `errors`.
    return firstNonEmptyString([record.message, record.detail])
        ?? fromValidationProblemDetails(record)
        ?? firstNonEmptyString([record.title]);
}

export function resolveApiErrorMessage(error: unknown, fallback: string): string {
    const candidate = error as ErrorLike | null;
    const status = candidate?.response?.status;

    // The rate limiter writes its own quota text as the body; never show an admin the raw limits.
    if (status === 429) return statusMessages[429];

    const fromResponseBody = fromBody(candidate?.response?.data);
    if (fromResponseBody) return fromResponseBody;

    if (typeof status === "number") {
        if (statusMessages[status]) return statusMessages[status];
        if (status >= 500) return "The server couldn't complete that request. Try again shortly.";
    }

    if (candidate?.isAxiosError === true && candidate.response === undefined) {
        return "Couldn't reach the server. Check your connection and try again.";
    }

    return fallback;
}
